import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import MarketplaceTopNavigation from "@/components/MarketplaceTopNavigation";
import BottomNavigation from "@/components/BottomNavigation";
import { SellerCard } from "@/components/marketplace/SellerCard";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

import localMarketsImg from '@/assets/category-local-markets.jpg';
import beautyImg from '@/assets/category-beauty.jpg';
import technologyImg from '@/assets/category-technology.webp';
import clothingImg from '@/assets/category-clothing.jpg';
import homeEssentialsImg from '@/assets/category-home-essentials.webp';
import servicesImg from '@/assets/category-services.webp';

interface Seller {
  id: string;
  name: string;
  image_url: string | null;
  rating: number;
  address: string;
}

export const MarketplaceCategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState("");
  const [categoryBanner, setCategoryBanner] = useState("");

  useEffect(() => {
    const fetchSellers = async () => {
      if (!categoryId) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("restaurants")
          .select("id, name, image_url, rating, address")
          .eq("business_type", "marketplace")
          .contains("categories", [categoryId]);

        if (error) throw error;
        // Sort: vendors with logos first
        const sorted = (data || []).sort((a, b) => {
          const aHasLogo = a.image_url && a.image_url !== '/placeholder.svg' ? 1 : 0;
          const bHasLogo = b.image_url && b.image_url !== '/placeholder.svg' ? 1 : 0;
          return bHasLogo - aHasLogo;
        });
        setSellers(sorted);

        // Set category name and banner based on categoryId
        const categoryData: Record<string, { name: string; banner: string }> = {
          groceries: { name: "Groceries", banner: localMarketsImg },
          beauty: { name: "Beauty", banner: beautyImg },
          technology: { name: "Technology", banner: technologyImg },
          clothing: { name: "Clothing", banner: clothingImg },
          home_essentials: { name: "Home Essentials", banner: homeEssentialsImg },
          services: { name: "Services", banner: servicesImg },
        };
        
        const category = categoryData[categoryId];
        if (category) {
          setCategoryName(category.name);
          setCategoryBanner(category.banner);
        }
      } catch (error) {
        console.error("Error fetching sellers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, [categoryId]);

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceTopNavigation />
      
      <div className="pb-24">
        {/* Category Banner */}
        {categoryBanner && (
          <div className="relative h-48 w-full overflow-hidden">
            <img 
              src={categoryBanner} 
              alt={categoryName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
            
            {/* Back button at top left */}
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="absolute top-4 left-4 bg-background/90 hover:bg-background text-foreground"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        )}
        
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-foreground my-8">
            {categoryName} Sellers
          </h1>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading sellers...</p>
            </div>
          ) : sellers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No sellers found in this category yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sellers.map((seller) => (
                <SellerCard key={seller.id} seller={seller} />
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};
